import { useEffect, useState } from "react";

import classes from './DynamicText.module.scss';


interface TextStyleFields {
    text?: string,
    textColor?: string,
    textSize?: string,
    textWeight?: string,
}

interface DescObj extends TextStyleFields {
    isLink: boolean,
    descLinkTarget?: string,
}


interface TextData {  
    visible: boolean,
    showBullet: boolean,
    background:string,
    borderColor:string,
    icon: boolean, 
    iconColor: string, 
    title: TextStyleFields,
    desc: DescObj[][],
    link: string,
    linkTarget: string
}

declare var _curLang: any;
const DEFAULT_COLOR = "inherit";
const DEFAULT_SIZE = "16px";
const DEFAULT_WEIGHT = "400";

// Shared cache of in-flight/resolved fetches per language,
// so multiple DynamicText instances only trigger one network request.
const dynamicTextCache: Record<string, Promise<any>> = {};

const loadDynamicText = (lang: string): Promise<any> => {
    if (!dynamicTextCache[lang]) {
        dynamicTextCache[lang] = fetch(
            `${process.env.PUBLIC_URL}/dynamic_translations/${lang}.json?v=${Date.now()}`
        )
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error ${res.status}`);
                }
                return res.json();
            })
            .catch((err) => {
                // Clear the failed entry so a later mount can retry
                delete dynamicTextCache[lang];
                throw err;
            });
    }
    return dynamicTextCache[lang];
};


const DynamicText = props => {
    const [dataText,setDataText] = useState<TextData | null>(null);
       
    useEffect(() => {
        loadDynamicText(_curLang)
          .then((data) => {                        
            setDataText(data[props.page]);
          })
          .catch((err) => {
            console.error("Failed to load JSON:", err);
          });
    }, [_curLang]);


    const linkClickHandler = () => {
        if(dataText?.linkTarget){
            window.open(dataText?.linkTarget, "_blank");    
        }         
    };

    const linkPressHandler = (event) => {
        event.preventDefault();
        if (event.charCode === 13 || event.charCode === 32){
            linkClickHandler();
        }        
    };

    const descLinkClickHandler = (target?: string) => {
        if (target) {
            window.open(target, "_blank");
        }
    }

    const descLinkPressHandler = (event, target?: string) => {
        event.preventDefault();
        if (event.charCode === 13 || event.charCode === 32){
            descLinkClickHandler(target);
        }        
    };
    
    const getTextStyle = (obj: TextStyleFields): React.CSSProperties => ({
        color: obj.textColor || DEFAULT_COLOR,
        fontSize: obj.textSize || DEFAULT_SIZE,
        fontWeight: obj.textWeight || DEFAULT_WEIGHT,
    });


      return (   
        <>
        {dataText?.visible &&
            <div className={classes.container}  
                style={{
                    backgroundColor: dataText?.background === "" ? "transparent" : dataText?.background,
                    borderColor: dataText?.borderColor === "" ? "#2c69c9" : dataText?.borderColor
                }}>

                {dataText?.icon &&
                    <div>
                        <span className={classes.alertIcon} aria-hidden="true"
                            style={{  
                                backgroundColor: dataText.iconColor === "" ? "#354f7a" : dataText.iconColor 
                            }}>                                
                        </span>
                    </div>
                }

                <div className={classes.main}>

                    {dataText?.title?.text &&
                        <div 
                            className={classes.title}
                            style={getTextStyle(dataText.title)}
                        >
                            {dataText.title.text}
                        </div>
                    }

                    {dataText?.desc?.map((objArr: DescObj[], arrIndex) => (
                        <div key={arrIndex} className={classes.desc}>
                            {dataText.desc.length > 1 && dataText.showBullet && (
                                <span className={classes.bullet}>&#x2022;</span>
                            )}
                        
                            {objArr.map((obj: DescObj, index) =>
                                obj.isLink ? (
                                    <span
                                        key={`${arrIndex}-${index}`}
                                        className={classes.desc_link}
                                        style={getTextStyle(obj)}
                                        role="link"
                                        tabIndex={0}
                                        onClick={() => descLinkClickHandler(obj.descLinkTarget)}
                                        onKeyPress={(event) => descLinkPressHandler(event, obj.descLinkTarget)}
                                    >
                                        {obj.text}
                                    </span>                                     
                                ) : (
                                    <span 
                                        className={classes.desc_text} 
                                        key={`${arrIndex}-${index}`}
                                        style={getTextStyle(obj)}
                                    >
                                        {obj.text}
                                    </span>
                                )
                            )}
                        </div>
                    ))}

                    {dataText?.link && 
                        <div 
                            className={classes.link}
                            id="loginDynamicLink" 
                            role="link" 
                            tabIndex={0} 
                            onClick={linkClickHandler}
                            onKeyPress={linkPressHandler}
                        >
                            {dataText?.link}
                        </div>
                    }

                    
                </div>
            </div>
        }
        </>
      );

};
export default DynamicText;